import React, { useState, useMemo, useEffect, } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, } from 'reactstrap';
import { parse as parseCsv, unparse as unparseCsv, } from 'papaparse';
import { isEmpty, fromPairs, } from 'lodash';
import { format as formatDate, } from 'date-fns';
import { toast } from 'react-toastify';
import XLSX from 'xlsx';

import firebase from '../../firebase';
import { readFile } from '../../utils';
import useCompany from '../hooks/useCompany';
import useFirebaseUser from '../hooks/useFirebaseUser';

const { entries } = Object;
const storageRef = firebase.storage().ref();

export default function ImportMappingModal({
  isOpen,
  toggle,
  fields = {},
  onImport,
  encoding = 'Shift_JIS',
  storageKey,
  importKey,
}) {
  const company = useCompany();
  const { firebaseUser } = useFirebaseUser();
  const [file, setFile] = useState(null);
  const [rawCsv, setRawCsv] = useState(null);
  const [skipRows, setSkipRows] = useState(0);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [isImporting, setIsImporting] = useState(false);
  const [workbook, setWorkbook] = useState(null);

  const fieldEntries = useMemo(_ => entries(fields), [fields]);

  useEffect(_ => {
    if (isOpen && storageKey) {
      const savedMapping = localStorage.getItem(storageKey);
      if (savedMapping) {
        try {
          setMapping(JSON.parse(savedMapping));
        } catch (e) {
          console.error('Failed to parse saved mapping', e);
        }
      }
    }
  }, [isOpen, storageKey]);

  // 指定した行を項目名（ヘッダー）行として、それより前の行を読み飛ばしてパース
  const dropHeadRows = (csvText, headerRowIndex) => {
    if (!headerRowIndex) return csvText;
    const { data: lines } = parseCsv(csvText, { header: false });
    return unparseCsv(lines.slice(headerRowIndex));
  };

  // 項目名行（マッピング対象フィールド名/ラベルを含む行）を探し、初期選択として提案
  // 検出できない場合は1行目（インデックス0）をデフォルトとする
  const detectHeaderRow = (csvText) => {
    const { data: lines } = parseCsv(csvText, { header: false });
    const fieldKeywords = fieldEntries.flatMap(([fieldName, fieldConfig]) => [fieldName, fieldConfig.label].filter(Boolean));
    const headerIndex = lines.findIndex(cells => cells.some(cell => fieldKeywords.includes((cell || '').trim())));
    return headerIndex > 0 ? headerIndex : 0;
  };

  const parseCsvFromHeaderRow = (csvText, headerRowIndex) => {
    // 空の項目名や同名の列を区別できるよう、列は位置インデックスで扱う
    const { data: lines } = parseCsv(dropHeadRows(csvText, headerRowIndex), { header: false });

    if (lines.length > 0) {
      const headerLine = lines[0];
      // value=列インデックス(文字列), label=項目名（空なら「列N」）
      const headers = headerLine.map((name, index) => ({
        value: String(index),
        label: (name || '').trim() || `列${index + 1}`,
        name: (name || '').trim(),
      }));
      setCsvHeaders(headers);

      const dataRows = lines.slice(1)
        .map(cells => fromPairs(cells.map((cell, index) => [String(index), cell])))
        .filter(_ => !Object.values(_).every(isEmpty));
      setCsvRows(dataRows);

      const autoMapping = fromPairs(
        fieldEntries.map(([fieldName, fieldConfig]) => {
          const matched = headers.find(_ =>
            _.name && (_.name === fieldName || _.name === fieldConfig.label)
          );
          return [fieldName, matched ? matched.value : ''];
        })
      );
      setMapping(x => ({ ...autoMapping, ...x }));
    } else {
      setCsvHeaders([]);
      setCsvRows([]);
    }
  };

  const loadCsvData = (csvText) => {
    setRawCsv(csvText);
    const detected = detectHeaderRow(csvText);
    setSkipRows(detected);
    parseCsvFromHeaderRow(csvText, detected);
  };

  const onSelectHeaderRow = (headerRowIndex) => {
    setSkipRows(headerRowIndex);
    if (rawCsv != null) parseCsvFromHeaderRow(rawCsv, headerRowIndex);
  };

  const onSelectFile = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    try {
      const arrayBuffer = await readFile(selectedFile, 'readAsArrayBuffer');

      if (selectedFile.name.toLowerCase().endsWith('.csv')) {
        let fileContent;
        try {
          const utf8Decoder = new TextDecoder('UTF-8', { fatal: true });
          fileContent = utf8Decoder.decode(arrayBuffer);
        } catch (e) {
          const sjisDecoder = new TextDecoder(encoding);
          fileContent = sjisDecoder.decode(arrayBuffer);
        }
        loadCsvData(fileContent);
      } else {
        setWorkbook(XLSX.read(arrayBuffer));
      }
    } catch (e) {
      console.error('Failed to read file', e);
      toast.error('ファイルの読み込みに失敗しました');
    }
  };

  const onSelectSheet = (sheetName) => {
    const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
    loadCsvData(csv);
    setWorkbook(null);
  };

  const updateMapping = (fieldName, csvColumn) => {
    setMapping(x => ({ ...x, [fieldName]: csvColumn }));
  };

  const handleImport = async () => {
    const allFieldsMapped = fieldEntries
      .filter(([, fieldConfig]) => fieldConfig.validations?.required)
      .every(([fieldName]) => mapping[fieldName]);

    if (!allFieldsMapped) {
      toast.error('必須項目をすべてマッピングしてください');
      return;
    }

    setIsImporting(true);

    try {
      const mappedRows = csvRows.map(_ =>
        fromPairs(
          entries(mapping)
            .filter(([, csvColumn]) => csvColumn !== '' && csvColumn != null)
            .map(([fieldName, csvColumn]) => [fieldName, _[csvColumn]])
        )
      );

      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(mapping));
      }

      const importRef = importKey ? company.ref.collection('imports').doc() : null;

      await onImport(mappedRows, importRef);

      if (importRef && file) {
        const storagePath = `companies/${company.id}/imports/${importRef.id}/${formatDate(new Date(), 'yyyyMMddHHmmss')}/${file.name}`;
        const fileRef = storageRef.child(storagePath);
        await fileRef.put(file, { name: file.name, contentType: file.type });
        await importRef.set({
          key: importKey,
          storagePath,
          fileName: file.name,
          fileType: file.type,
          createdBy: firebaseUser.uid,
          createdAt: new Date(),
        });
      }

      toast.success('インポートしました');

      setFile(null);
      setRawCsv(null);
      setSkipRows(0);
      setCsvHeaders([]);
      setCsvRows([]);
      setWorkbook(null);
      toggle();
    } catch (e) {
      console.error('Import failed', e);
      toast.error('インポートに失敗しました');
    }

    setIsImporting(false);
  };

  const handleClose = () => {
    setFile(null);
    setRawCsv(null);
    setSkipRows(0);
    setCsvHeaders([]);
    setCsvRows([]);
    setWorkbook(null);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader>インポート</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>ファイルを選択</Label>
          <div>
            <Input type="file" accept=".csv,.xls,.xlsx" onChange={onSelectFile} />
          </div>
        </FormGroup>

        {workbook != null && (
          <FormGroup>
            <Label>シートを選択</Label>
            <Input type="select" onChange={e => e.target.value && onSelectSheet(e.target.value)}>
              <option value="">選択してください</option>
              {workbook.SheetNames.map(_ => (
                <option key={_} value={_}>{_}</option>
              ))}
            </Input>
          </FormGroup>
        )}

        {rawCsv != null && (
          <FormGroup className="mt-3">
            <Label>項目名（見出し）の行を選択</Label>
            <small className="text-muted d-block mb-2">
              項目名の行をクリックしてください。それより前の行（タイトル行・空行など）は読み飛ばします
            </small>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <tbody>
                  {parseCsv(rawCsv, { header: false }).data.slice(0, skipRows + 6).map((cells, index) => {
                    const isHeader = index === skipRows;
                    const isExcluded = index < skipRows;
                    return (
                      <tr
                        key={index}
                        onClick={_ => onSelectHeaderRow(index)}
                        className={isHeader ? 'table-primary' : (isExcluded ? 'text-muted' : '')}
                        style={{ cursor: 'pointer', ...(isExcluded ? { textDecoration: 'line-through', background: '#f8f9fa' } : {}) }}
                      >
                        <td className="small text-muted text-nowrap">
                          {index + 1}{isHeader ? '（項目名）' : (isExcluded ? '（除外）' : '')}
                        </td>
                        {cells.slice(0, 8).map((cell, i) => (
                          <td key={i} className="small">{cell}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </FormGroup>
        )}

        {csvHeaders.length > 0 && (
          <div className="mt-4">
            <h6>列のマッピング</h6>
            <p className="text-muted small">
              各項目に対応するファイルの列を選択してください
            </p>
            {fieldEntries.map(([fieldName, fieldConfig]) => {
              const isRequired = fieldConfig.validations?.required;
              return (
                <FormGroup key={fieldName}>
                  <Label>
                    {fieldConfig.label || fieldName}
                    {isRequired && <span className="text-danger ml-1">*</span>}
                  </Label>
                  <Input
                    type="select"
                    value={mapping[fieldName] || ''}
                    onChange={e => updateMapping(fieldName, e.target.value)}
                  >
                    <option value="">選択してください</option>
                    {csvHeaders.map(_ => (
                      <option key={_.value} value={_.value}>{_.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              );
            })}
            <div className="mt-3">
              <h6>プレビュー</h6>
              <p className="text-muted small">
                {csvRows.length}行のデータ（最大5行まで表示）
              </p>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead className="thead-light">
                    <tr>
                      {fieldEntries.map(([fieldName, fieldConfig]) => (
                        <th key={fieldName}>{fieldConfig.label || fieldName}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.slice(0, 5).map((row, index) => (
                      <tr key={index}>
                        {fieldEntries.map(([fieldName]) => {
                          const csvColumn = mapping[fieldName];
                          const value = csvColumn !== '' && csvColumn != null ? row[csvColumn] : '';
                          return (
                            <td key={fieldName} className="small">
                              {value || <span className="text-muted">-</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isImporting}>
          キャンセル
        </Button>
        <Button
          color="primary"
          onClick={handleImport}
          disabled={!file || csvHeaders.length === 0 || isImporting}
        >
          {isImporting ? 'インポート中...' : 'インポート'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
